import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  getBucketDetailsAsync,
  selectBucketDetails,
  selectBucketDetailsLoading,
} from "../../store/s3/buckets/bucketSlice";
import { selectActionLoading } from "../../store/s3/buckets/bucketsSlice";
import RightArrow from "./../../assets/icons/arrow-right.svg?react";
import RefreshIcon from "./../../assets/icons/arrow-path.svg?react";
import DeleteIcon from "./../../assets/icons/archive-box.svg?react";
import FolderOpenIcon from "./../../assets/icons/folder-open.svg?react";
import { S3BucketUrl, S3BucketsUrl } from "../../utils/urls";
import S3BucketOverviewTab from "../../components/s3/buckets/settings/S3BucketOverviewTab";
import S3BucketEventsTab from "../../components/s3/buckets/settings/S3BucketEventsTab";
import S3BucketAccessLogsTab from "../../components/s3/buckets/settings/S3BucketAccessLogsTab";
import { DeleteModal } from "../../components/modals/QuestionModal";
import {
  deleteBucketAsync,
  getBucketsAsync,
} from "../../store/s3/buckets/bucketsSlice";
import S3BucketAnonymousTab from "../../components/s3/buckets/settings/S3BucketAnonymousTab";
import S3BucketLifecycleTab from "../../components/s3/buckets/settings/S3BucketLifecycleTab";
import S3BucketReplicationsTab from "../../components/s3/buckets/settings/S3BucketReplicationsTab";
import { Helmet } from "react-helmet";
import ComputerIcon from "./../../assets/icons/computer-desktop.svg?react";
import BeatIcon from "./../../assets/icons/beat.svg?react";
import BellIcon from "./../../assets/icons/bell.svg?react";
import AnonymousIcon from "./../../assets/icons/anonymous.svg?react";
import { Button, Skeleton, TabOptions, Tabs, Typography } from "djuno-design";
import useServiceType from "../../hooks/useServiceType";
import { useTour } from "../../providers/TourProvider";

export const S3BucketSettinsMenus: TabOptions = [
  {
    label: (
      <div className="flex items-center gap-1">
        <ComputerIcon className="w-3" />
        Overview
      </div>
    ),
    element: <S3BucketOverviewTab />,
    testId: "1th-tab",
  },
  {
    label: (
      <div className="flex items-center gap-1 events">
        <BellIcon className="w-3" />
        Events
      </div>
    ),
    element: <S3BucketEventsTab />,
    testId: "2th-tab",
  },
  {
    label: (
      <div className="flex items-center gap-1 replications">Replications</div>
    ),
    element: <S3BucketReplicationsTab />,
    testId: "3th-tab",
  },
  {
    label: <div className="flex items-center gap-1 lifecycle">Lifecycle</div>,
    element: <S3BucketLifecycleTab />,
    testId: "4th-tab",
  },
  {
    label: (
      <div className="flex items-center gap-1 access-logs">
        <BeatIcon className="w-3" />
        Access logs
      </div>
    ),
    element: <S3BucketAccessLogsTab />,
    testId: "5th-tab",
  },
  {
    label: (
      <div className="flex items-center gap-1 anonymose">
        <AnonymousIcon className="w-3" />
        Anonymous
      </div>
    ),
    element: <S3BucketAnonymousTab />,
    testId: "6th-tab",
  },
];

const S3BucketSettingPage = () => {
  const { bucketName } = useParams();
  const bucketDetails = useAppSelector(selectBucketDetails);
  const bucketDetailsLoading = useAppSelector(selectBucketDetailsLoading);
  const [deleteBucketName, setDeleteBucketName] = useState<string | null>(null);
  const actionLoading = useAppSelector(selectActionLoading);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { run } = useTour("bucket-setting");

  const { serviceType } = useServiceType(
    import.meta.env.VITE_SIMPLE_STORAGE_SERVICE_TYPE
  );

  useEffect(() => {
    if (bucketName) {
      dispatch(getBucketDetailsAsync({ name: bucketName }));
    }
  }, [bucketName, dispatch]);

  const handleDelete = async () => {
    if (!deleteBucketName) return;

    const deleteAction = await dispatch(
      deleteBucketAsync({ name: deleteBucketName })
    );

    if (deleteAction.type === "buckets/delete/fulfilled") {
      const fetchAction = await dispatch(getBucketsAsync());

      if (fetchAction.type === "buckets/fulfilled") {
        navigate(S3BucketsUrl);
      }
    }

    setDeleteBucketName(null);
  };

  return (
    <>
      <Helmet>
        <title>
          {import.meta.env.VITE_APP_NAME} | {serviceType?.ServiceName || ""} (
          {bucketDetails ? bucketDetails.name : ""} settings)
        </title>
        <meta
          name="description"
          content="Simple-storage technology refers to a straightforward method of storing data or information in a manner that is easy to implement and access"
        />
      </Helmet>
      <div className="flex w-full h-16">
        <div className="px-6 items-center justify-between flex w-full">
          <div className="flex items-center gap-2">
            <RightArrow
              onClick={() => navigate(S3BucketsUrl)}
              className="rotate-180 w-5 h-5 hover:scale-110 transition-all duration-500 text-slate-800 dark:text-slate-100 cursor-pointer"
            />
            {bucketDetailsLoading && (
              <Skeleton style={{ width: 250, height: 26 }} />
            )}
            {!bucketDetailsLoading && (
              <Typography.Title
                level={5}
                className="!mb-0 !max-w-[200px] md:!max-w-[400px] lg:!max-w-[350px] xl:!max-w-[400px] !truncate"
              >
                {bucketDetails?.name}
              </Typography.Title>
            )}
          </div>
          <div className="ml-auto flex items-center justify-end gap-1 md:gap-2 bucket-setting-header">
            <Button
              onClick={() => {
                run();
              }}
            >
              Start Tour
            </Button>
            <Button
              uiType="light"
              onClick={() => bucketName && navigate(S3BucketUrl(bucketName))}
              className="group browse-button"
              tooltip={{ content: "Browse bucket" }}
            >
              <FolderOpenIcon className="w-4 h-4 group-hover:scale-110 transition-all duration-500" />
            </Button>
            <Button
              uiType="light"
              onClick={() =>
                bucketName &&
                dispatch(getBucketDetailsAsync({ name: bucketName }))
              }
              className="group refresh-button"
              tooltip={{ content: "Refresh" }}
            >
              <RefreshIcon className="w-4 h-4 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
            </Button>
            <Button
              uiType="dangerLight"
              onClick={() => bucketName && setDeleteBucketName(bucketName)}
              className="delete-button"
            >
              <span className="hidden md:block">Delete Bucket</span>
              <DeleteIcon className="w-4 h-4 group-hover:scale-110 transition-all duration-500" />
            </Button>
          </div>
        </div>
      </div>
      <div className="mx-6">
        <Tabs
          options={S3BucketSettinsMenus.map((o) => ({
            label: o.label,
            element: o.element,
            testId: o.testId,
          }))}
          panelClassName="!bg-transparent"
        />
      </div>

      <DeleteModal
        title="Delete Bucket"
        isOpen={deleteBucketName !== null}
        onClose={() => setDeleteBucketName(null)}
        onConfirm={handleDelete}
        loading={actionLoading}
        confirmString={bucketDetails?.name}
        confirmButtonType="danger"
      />
    </>
  );
};
export default S3BucketSettingPage;
